import { Helmet } from 'react-helmet-async';

interface LocalBusinessProps {
  type?: 'LocalBusiness' | 'TravelAgency' | 'TouristInformationCenter';
}

export const LocalBusinessStructuredData = ({ type = 'TravelAgency' }: LocalBusinessProps) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": type,
    "name": "Roca Rio",
    "description": "Agence de tourisme locale et engagée à Rio de Janeiro. Excursions authentiques et solidaires avec guide francophone certifiée.",
    "url": "https://rocario-cleacarioca.com",
    "logo": "https://rocario-cleacarioca.com/hero-home.webp",
    "image": "https://rocario-cleacarioca.com/hero-home.webp",
    "telephone": "+55-21-97679-5050",
    "email": "contact.rocario@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Rio de Janeiro",
      "addressRegion": "RJ",
      "addressCountry": "BR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-22.9068",
      "longitude": "-43.1729"
    },
    "priceRange": "€€",
    "currenciesAccepted": "BRL, EUR",
    "paymentAccepted": "Cash, Credit Card",
    "openingHours": "Mo-Su 08:00-20:00",
    "sameAs": [
      "https://www.instagram.com/rocariovoyages",
      "https://wa.me/5521976795050"
    ],
    "areaServed": {
      "@type": "City",
      "name": "Rio de Janeiro"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

interface TouristAttractionProps {
  name: string;
  description: string;
  url: string;
  image?: string;
  price?: number;
  duration?: string;
  category?: string;
  rating?: number;
  reviewCount?: number;
}

export const TouristAttractionStructuredData = ({
  name,
  description,
  url,
  image,
  price,
  duration,
  category,
  rating = 5,
  reviewCount = 25
}: TouristAttractionProps) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": name,
    "description": description,
    "url": url,
    ...(image && { "image": image }),
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Rio de Janeiro",
      "addressRegion": "RJ",
      "addressCountry": "BR"
    },
    "touristType": ["Individual", "Group", "Family"],
    ...(category && { "additionalType": category }),
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": rating,
      "bestRating": 5,
      "worstRating": 1,
      "ratingCount": reviewCount
    },
    "offers": {
      "@type": "Offer",
      ...(price && {
        "price": price,
        "priceCurrency": "EUR"
      }),
      "availability": "https://schema.org/InStock",
      "validFrom": new Date().toISOString().split('T')[0],
      "seller": {
        "@type": "TravelAgency",
        "name": "Roca Rio",
        "telephone": "+55-21-97679-5050"
      }
    },
    ...(duration && {
      "duration": duration
    })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbStructuredDataProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbStructuredData = ({ items }: BreadcrumbStructuredDataProps) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

interface FAQStructuredDataProps {
  faqs: Array<{
    question: string;
    answer: string;
  }>;
}

export const FAQStructuredData = ({ faqs }: FAQStructuredDataProps) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};
